import { AlertTriangle, Info } from "lucide-react";
import { Link } from "@tanstack/react-router";
import { useState } from "react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { FullDisclosureModal } from "./full-disclosure-modal";

/**
 * Prominent risk disclosure for marketing surfaces (homepage, pricing, signup, login).
 */
export function HomepageDisclaimer() {
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  return (
    <>
      <div
        className="border-y border-warn/30 bg-warn/10 px-3 py-2 text-center text-[11px] leading-snug text-warn sm:px-6 sm:py-3 sm:text-[12px]"
        role="note"
        aria-label="Risk disclosure"
      >
        <div className="mx-auto flex max-w-4xl items-start justify-center gap-2">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <p className="text-left sm:text-center">
            <span className="font-semibold">
              ForexMind AI provides AI-powered market intelligence for educational purposes only.
            </span>{" "}
            <span className={expanded ? "inline" : "hidden sm:inline"}>
              This is not financial advice. Forex and CFD trading involves significant risk of loss.
              Past performance does not guarantee future results. Only trade with capital you can
              afford to lose. Access may be restricted in certain jurisdictions.{" "}
              <button
                type="button"
                onClick={() => setOpen(true)}
                className="underline underline-offset-2 hover:text-foreground"
              >
                Read Full Risk Disclosure
              </button>
              .
            </span>
            {!expanded && (
              <button
                type="button"
                onClick={() => setExpanded(true)}
                className="ml-1 underline underline-offset-2 hover:text-foreground sm:hidden"
                aria-label="Expand risk disclosure"
              >
                More
              </button>
            )}
          </p>
        </div>
      </div>
      <FullDisclosureModal open={open} onOpenChange={setOpen} />
    </>
  );
}

/**
 * Compact educational-only notice attached to every market-intelligence verdict.
 * Includes an info tooltip clarifying the non-advisory relationship.
 */
export function SignalDisclaimer({ className }: { className?: string }) {
  return (
    <div
      className={`flex items-center justify-center gap-1.5 border-t border-border/60 px-5 py-2 text-[10.5px] text-muted-foreground ${className ?? ""}`}
      role="note"
    >
      <span aria-hidden>⚠️</span>
      <span>Educational market intelligence only — not financial advice.</span>
      <Tooltip>
        <TooltipTrigger asChild>
          <button type="button" aria-label="Learn more">
            <Info className="h-3 w-3 cursor-help text-muted-foreground/70 hover:text-muted-foreground" />
          </button>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-[260px] text-center leading-snug">
          ForexMind AI does not execute trades, manage funds, or provide personalized investment
          advice. Users are responsible for their own trading decisions.
        </TooltipContent>
      </Tooltip>
    </div>
  );
}

/** Telegram footer block appended to every outbound message. */
export { CANONICAL_TELEGRAM_DISCLAIMER as TELEGRAM_DISCLAIMER } from "@/lib/compliance/constants";
